import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Phone, User, MessageSquare, Send } from 'lucide-react';
import ThankYouModal from './ThankYouModal';

const Contact = () => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    tour: '',
    passengers: '',
    date: '',
    time: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isBookingMode, setIsBookingMode] = useState(false);

  // Auto-select tour from URL parameter and enable booking mode
  useEffect(() => {
    // Get the hash and query string from the URL
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
    const tourParam = searchParams.get('tour');
    
    if (tourParam) {
      setIsBookingMode(true);
      setFormData(prev => ({
        ...prev,
        tour: tourParam
      }));
      
      // Scroll to contact form
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.hash, location.search]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Endpoint: PHP handler on production (/api/contact.php), FastAPI fallback in dev
      const CONTACT_ENDPOINT =
        process.env.REACT_APP_CONTACT_ENDPOINT ||
        (process.env.REACT_APP_BACKEND_URL
          ? `${process.env.REACT_APP_BACKEND_URL}/api/contact`
          : '/api/contact.php');

      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormData({ name: '', email: '', phone: '', tour: '', date: '', time: '', message: '' });
        setShowModal(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus({ 
          type: 'error', 
          message: errorData.detail || errorData.message || 'Failed to send message. Please try again.' 
        });
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <Mail className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 font-semibold text-sm uppercase tracking-wide">Contact Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Input */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.name}
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                    placeholder={t.contact.name}
                  />
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.email}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                    placeholder={t.contact.email}
                  />
                </div>
              </div>

              {/* Phone Input */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.phone}
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                    placeholder={t.contact.phone}
                  />
                </div>
              </div>

              {/* Booking-specific fields - only show when coming from tour page */}
              {isBookingMode && (
                <>
                  {/* Tour Selection */}
                  <div>
                    <label htmlFor="tour" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t.contact.tourSelection}
                    </label>
                    <select
                      id="tour"
                      name="tour"
                      value={formData.tour}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none appearance-none bg-white"
                    >
                      <option value="">{t.contact.tourPlaceholder}</option>
                      <option value="oneHour">{t.tours.oneHour.title}</option>
                      <option value="twoHours">{t.tours.twoHours.title}</option>
                      <option value="threeHours">{t.tours.threeHours.title}</option>
                    </select>
                  </div>

                  {/* Passengers Selection */}
                  <div>
                    <label htmlFor="passengers" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t.contact.passengers}
                    </label>
                    <select
                      id="passengers"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none appearance-none bg-white"
                    >
                      <option value="">{t.contact.passengersPlaceholder}</option>
                      <option value="1">1 Passenger</option>
                      <option value="2">2 Passengers</option>
                      <option value="3">3 Passengers</option>
                      <option value="4">4 Passengers</option>
                    </select>
                  </div>

                  {/* Date and Time Grid */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Date Input */}
                    <div>
                      <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t.contact.date}
                      </label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                      />
                    </div>

                    {/* Time Input */}
                    <div>
                      <label htmlFor="time" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t.contact.time}
                      </label>
                      <input
                        type="time"
                        id="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Message Textarea */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.contact.message}
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none resize-none"
                    placeholder={t.contact.message}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center space-x-3 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <span>{isSubmitting ? t.contact.sending : t.contact.send}</span>
                <Send className="w-5 h-5" />
              </button>

              {/* Status Message */}
              {status.message && (
                <div 
                  className={`p-4 rounded-lg text-center font-medium ${
                    status.type === 'success' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}
                >
                  {status.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Thank You Modal */}
      <ThankYouModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)}
        language={language}
      />
    </section>
  );
};

export default Contact;
